import { ComponentProps } from 'preact';
import { Chip } from '../UNSAFE_Chip';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '../resources/nls/bundle';
import { selectedValueCountStyles } from './themes/SelectedValuesCountStyles.css';

type PickedPropsFromChip = Pick<
  ComponentProps<typeof Chip>,
  'aria-label' | 'isDisabled' | 'isSelected' | 'onToggle'
>;

type Props = PickedPropsFromChip & {
  count?: number;
  onKeyDown?: (event: KeyboardEvent) => void;
  onKeyUp?: (event: KeyboardEvent) => void;
  onMouseDown?: (event: MouseEvent) => void;
};

// This component renders a badge showing the number of selected values
export function SelectedValuesCount({
  'aria-label': ariaLabel,
  count = 0,
  onKeyDown,
  onKeyUp,
  onMouseDown,
  ...passThroughProps
}: Props) {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const instructions = translations.selectMultiple_showSelectedValues();
  const accLabel = ariaLabel ? `${ariaLabel}. ` : '';

  const ninetyNinePlus = translations.selectMultiple_countPlus({ COUNT: `99` });
  // The Chip shows the number of values selected or 99+ if the number is over 99.
  const chipCount = count > 99 ? `${ninetyNinePlus}` : `${count}`;
  // The screenreader will hear what the number of selected values is along with instructions
  // like Click to see the full list.
  const valuesSelectedText = translations.selectMultiple_valuesSelected({
    VALUE_COUNT: `${count}`
  });
  const label = `${accLabel}${valuesSelectedText} ${instructions}`;
  return (
    <div
      class={selectedValueCountStyles.base}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
      onMouseDown={onMouseDown}>
      <Chip aria-label={label} {...passThroughProps}>
        {chipCount}
      </Chip>
    </div>
  );
}
